import axios from "axios";
import { DATA_SVC_RS_ADDRESS } from "constants/app.constants";

import {
  ArpSegment,
  ForecastConstant,
  ForecastItem,
  WellData
} from "../models/UserArpsModel";

const arpsServiceUrl = process.env.REACT_APP_USER_ARPS_SERVICE;

export interface TypeWellId {
  id: string;
  folderId: string;
  name: string;
}

export interface ExportForecastRequest {
  filterId: string;
  format?: "CSV" | "XML";
  settings?: ForecastExportSettings;
  typeWellIds: TypeWellId[];
}

export interface ForecastExportSettings {
  thirdPartyFormat?: string;
  source?: string;
}

export interface UploadForecastRequest {
  projectId: string;
  uploadType: string;
  folderName: string;
  folderId: string;
  formData: FormData;
}

export interface UploadForecastWithMappingsRequest {
  projectId: string;
  formData: FormData;
}

/**
 * Every reserve category, as a value rather than only a type, so callers that have to recognise
 * one in a string can enumerate them. The type is derived from this array rather than declared
 * beside it, so the two cannot drift.
 */
export const ALL_RESCATS = [
  "P+PDP",
  "P+P+PDP",
  "PDP",
  "TP",
  "TPP",
  "TPPP",
  "PD",
  "P+PD",
  "P+P+PD"
] as const;

export type Rescat = (typeof ALL_RESCATS)[number];

export interface ValNavFolderRequest {
  folderId: string;
  projectId: string;
  oldForecasts: string[];
  forecasts: string[];
  forecastIds: string[];
  wellData: { [id: string]: WellData };
  segments: { [id: string]: ArpSegment[] };
  constants: { [id: string]: ForecastConstant[] };
  reserveCategory: Rescat;
  source: string;
  composeUniqueIdFromWellName: boolean;
}

export interface ValNavFolderBulkRequest {
  folderId: string;
  projectId: string;
  oldForecasts: string[];
  forecasts: ForecastItem[];
}
export interface UpdateForecastResponse {
  message: string;
}

async function exportForecasts(request: ExportForecastRequest) {
  return axios.post(
    `${DATA_SVC_RS_ADDRESS}/api/v1/data/forecasts/export?exportFormat=${request.format}`,
    request,
    {
      responseType: "blob"
    }
  );
}

async function updatePerformanceData(folderName: string) {
  return axios.post(`${DATA_SVC_RS_ADDRESS}/api/v1/data/performance-data`, {
    FolderName: folderName
  });
}

async function parseAndDetectForecastProducts(request: UploadForecastRequest) {
  return axios.post(
    `${arpsServiceUrl}/project/${request.projectId}/detect-products`,
    request.formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }
  );
}

async function uploadArpsWithMappings(request: UploadForecastWithMappingsRequest) {
  return axios.post(
    `${arpsServiceUrl}/project/${request.projectId}/upload-arps`,
    request.formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }
  );
}

export interface UploadWellDataRequest {
  projectId: string;
  formData: FormData; // Contains: upload (file), folderId, mappings (JSON string)
}

export interface UploadWellDataResponse {
  updated: number;
  skipped: number;
}

/**
 * Upload well data (normalization parameters) for type wells in bulk.
 * The file should contain "Well Name" and "Reserve Category" columns for matching,
 * plus additional columns that get mapped to well data fields.
 */
async function uploadWellData(
  request: UploadWellDataRequest
): Promise<{ data: UploadWellDataResponse }> {
  return axios.post(
    `${arpsServiceUrl}/project/${request.projectId}/upload-well-data`,
    request.formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }
  );
}

async function saveValNavFolder(request: ValNavFolderRequest) {
  return axios.post(
    `${arpsServiceUrl}/project/${request.projectId}/valnav-type-wells`,
    request
  );
}

async function saveValNavFolderBulk(request: ValNavFolderBulkRequest) {
  return axios.post(
    `${arpsServiceUrl}/project/${request.projectId}/valnav-type-wells-bulk`,
    request
  );
}

export {
  exportForecasts,
  saveValNavFolder,
  updatePerformanceData,
  parseAndDetectForecastProducts,
  uploadArpsWithMappings,
  uploadWellData,
  saveValNavFolderBulk
};
